import React, { FC } from "react";
import { useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { LIFTS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/LiftColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/LiftSearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getLifts, deleteLift } from "../services/liftService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getBuildingNamesWithPagination } from "../services/buildingService";
import BuildingSearch from "../models/search/BuildingSearch";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";

interface LiftTabProps {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: any) => void;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    setBusyMessage: React.Dispatch<React.SetStateAction<string>>;
}

const LiftTab: FC<LiftTabProps> = ({ onFilterChange, setShowModal, setBusy, categories, setCategories, setBusyMessage }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.lifts);
    const itemPath = '/access-control/hardware/lift/';
    const lookup: Lookup[] = [
        {
            name: LookupTypes.buildingSite,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.building,
            values: [],
            dataMethod: getBuildingNamesWithPagination,
            searchAccessor: BuildingSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, LIFTS) => void) => {
        setDetails(userLoginId, LIFTS);
    }
    // <-- end context

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.deletingLiftPleaseWait));
        return new Promise((resolve, reject) => {
            deleteLift(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
                .finally(() => {
                    setBusyMessage('');
                    setBusy(false);
                });
        });
    }

    return <TableContextProvider
        dataMethod={getLifts}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        deleteMethod={onDelete}
        customDeleteMsgTrnKey={TranslationKeys.customDeleteLiftMsg}
        lookup={lookup}
        onAddNew={() => setShowModal(true)}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName="" />
    </TableContextProvider>;
};

export default LiftTab;
